import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Field of radial lines over a sphere ("hedgehog").
 * Each line grows outward according to its frequency band and the bass.
 *
 * WebGL ignores `gl_LineWidth` on virtually every platform (lines are always
 * 1px), so we render each line as a screen-space quad (two triangles) expanded
 * perpendicular to its projected direction by `uLineWidth` CSS pixels.
 */
export class LineField extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aFreqIndex;
        attribute float aIsOuter;       // 0 inner end, 1 outer end (this vertex)
        attribute float aOtherIsOuter;  // same flag for the segment's other end
        attribute float aSide;          // -1 / +1 (which side of the line)
        uniform float uTime, uBass, uBeat, uReactivity, uBassPunch, uLineWidth, uPixelRatio;
        uniform vec2 uResolution;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vMag;
        varying float vOuter;
        varying vec3 vDir;
        ${ZONE_GLSL_COMMON}
        void main() {
          float mag = texture2D(uFreq, vec2(aFreqIndex, 0.5)).r;
          vMag = mag;
          vOuter = aIsOuter;
          vec3 dir = normalize(position);
          vDir = dir;
          float grow = (mag * 2.4 + uBass * uBassPunch * 1.5 + uBeat * 0.8) * uReactivity;
          // Seed-driven beat punch only (uBeat decays to 0 between beats, so the
          // base growth stays original); the seed sets which spikes shoot out.
          float lobe = sin(dir.x * 6.0 + uSeed.x) * sin(dir.y * 6.0 + uSeed.y) * sin(dir.z * 6.0 + uSeed.z);
          grow += lobe * uSeedPulse * uReactivity * 1.0;
          // When zone reaction is on, hold a static hedgehog — only zones react.
          grow = mix(grow, 0.6, uZoneEnabled);
          grow += zoneDisp(dir);
          // Always-on idle wave so the spikes gently breathe even without audio.
          grow += (sin(dot(dir, vec3(4.0, 5.3, 3.7)) + uTime * 0.8)
                 + sin(dot(dir, vec3(6.1, 2.9, 5.1)) - uTime * 0.6)) * 0.05;
          // Project both segment ends; the outer end is the one that grows.
          vec4 clipThis  = projectionMatrix * modelViewMatrix * vec4(position + dir * grow * aIsOuter, 1.0);
          vec4 clipOther = projectionMatrix * modelViewMatrix * vec4(position + dir * grow * aOtherIsOuter, 1.0);
          // Expand into a quad of uLineWidth CSS px in screen space.
          vec2 screenThis  = (clipThis.xy  / clipThis.w  * 0.5 + 0.5) * uResolution;
          vec2 screenOther = (clipOther.xy / clipOther.w * 0.5 + 0.5) * uResolution;
          vec2 delta = screenOther - screenThis;
          vec2 dir2d = length(delta) > 1e-4 ? normalize(delta) : vec2(1.0, 0.0);
          vec2 normal = vec2(-dir2d.y, dir2d.x);
          vec2 screenPos = screenThis + normal * aSide * (uLineWidth * uPixelRatio * 0.5);
          vec2 ndc = screenPos / uResolution * 2.0 - 1.0;
          gl_Position = vec4(ndc * clipThis.w, clipThis.z, clipThis.w);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble, uLevel;
        varying float vMag;
        varying float vOuter;
        varying vec3 vDir;
        ${hsv2rgb}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float hue = fract(uHue + vMag * 0.3 + uTreble * 0.1);
          vec3 col = hsv2rgb(vec3(hue, 0.75, 0.5 + vMag * 0.5));
          // Brighter at the tip, fading toward the center.
          float alpha = mix(0.15, 0.9, vOuter) + uLevel * 0.2;
          // Zone reaction: tint the lines that fall inside a shape.
          vec4 z = zoneColor(vDir);
          vec3 outc = mix(applyPrimitiveTexture(col), z.rgb, z.a);
          gl_FragColor = vec4(outc, max(alpha, z.a));
        }
      `,
    })
    // uLineWidth isn't in the shared uniforms: we add it to this material.
    this.material.uniforms.uLineWidth = { value: 2 }

    this.object3D = new THREE.Mesh(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    this.material.uniforms.uLineWidth.value = params.lineWidth ?? 2
  }

  rebuild(params: VisualParams): void {
    const count = Math.floor(1500 * (params.density ?? 1))
    // 4 vertices + 2 triangles (6 indices) per line.
    const positions = new Float32Array(count * 4 * 3)
    const freqIndex = new Float32Array(count * 4)
    const isOuter = new Float32Array(count * 4)
    const otherIsOuter = new Float32Array(count * 4)
    const side = new Float32Array(count * 4)
    const indices = new Uint32Array(count * 6)
    const r0 = 1.6
    const golden = Math.PI * (3 - Math.sqrt(5))

    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2
      const r = Math.sqrt(1 - y * y)
      const theta = golden * i
      const dx = Math.cos(theta) * r
      const dy = y
      const dz = Math.sin(theta) * r
      const fi = Math.abs(y)
      // The 4 vertices share the same base position (dir * r0); the shader
      // displaces the outer ones and offsets each side. Layout per line:
      //   0 inner -side · 1 inner +side · 2 outer -side · 3 outer +side
      const cfg = [
        { outer: 0, other: 1, s: -1 },
        { outer: 0, other: 1, s: 1 },
        { outer: 1, other: 0, s: -1 },
        { outer: 1, other: 0, s: 1 },
      ]
      for (let k = 0; k < 4; k++) {
        const v = i * 4 + k
        positions[v * 3 + 0] = dx * r0
        positions[v * 3 + 1] = dy * r0
        positions[v * 3 + 2] = dz * r0
        freqIndex[v] = fi
        isOuter[v] = cfg[k].outer
        otherIsOuter[v] = cfg[k].other
        side[v] = cfg[k].s
      }
      const base = i * 4
      const idx = i * 6
      indices[idx + 0] = base + 0
      indices[idx + 1] = base + 2
      indices[idx + 2] = base + 1
      indices[idx + 3] = base + 2
      indices[idx + 4] = base + 3
      indices[idx + 5] = base + 1
    }

    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aFreqIndex', new THREE.BufferAttribute(freqIndex, 1))
    geo.setAttribute('aIsOuter', new THREE.BufferAttribute(isOuter, 1))
    geo.setAttribute('aOtherIsOuter', new THREE.BufferAttribute(otherIsOuter, 1))
    geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1))
    geo.setIndex(new THREE.BufferAttribute(indices, 1))
    geo.computeBoundingSphere()
  }
}
